import { Fragment, useMemo, type FC } from 'react';
import { useAtomValue } from 'jotai';
import { Layer, Source } from 'react-map-gl';
import { chunk } from 'lodash-es';
import type { Expression } from 'mapbox-gl';
import type { ColorByRule } from 'components/color-by-attribute-modal';
import { preDrillColorByUwiMapAtom } from './atoms';
import { buildColorByUwiColorMap, tileFingerprint } from './utils';

interface PreDrillScopedWellsLayerProps {
  /** All color rules; the enabled, map-targeting ones resolve to the painted
   *  UWIs (Well Qualifier is handled on the subject bore, not here). */
  colorRules: ColorByRule[];
}

const UWI_CHUNK_SIZE = 500;
// Matched-well emphasis: thicker, fully-opaque colored line.
const MATCHED_LINE_WIDTH = 4;
// survey_estimated: the line was built rather than measured, an operator's correction
// carried onto a well with no filed survey. A doc indexed before the field existed
// carries no value and reads as measured.
const MEASURED_SURVEY = ['!=', ['get', 'survey_estimated'], true];
const ESTIMATED_SURVEY = ['==', ['get', 'survey_estimated'], true];
// Fallback color for a tiled UWI with no explicit color (mapbox `match`
// requires a default).
const DEFAULT_WELL_COLOR = '#0F62FE';

// Build a mapbox `match` expression: group UWIs by color so we emit one case
// per distinct color rather than one per UWI.
function buildColorExpr(
  uwiColorMap: Record<string, string>
): string | Expression {
  const entries = Object.entries(uwiColorMap);
  if (entries.length === 0) return DEFAULT_WELL_COLOR;

  const colorGroups = new Map<string, string[]>();
  for (const [uwi, color] of entries) {
    const group = colorGroups.get(color);
    if (group) group.push(uwi);
    else colorGroups.set(color, [uwi]);
  }

  const flat: (string | string[])[] = [];
  for (const [color, uwis] of colorGroups) {
    flat.push(uwis.length === 1 ? uwis[0] : uwis, color);
  }
  return ['match', ['get', 'uwi'], ...flat, DEFAULT_WELL_COLOR] as Expression;
}

// Scoped-wells overlay for the Workspace Map: wells matched by the active
// color-by rules as MVT survey + stick lines, painted thicker over the base.
export const PreDrillScopedWellsLayer: FC<PreDrillScopedWellsLayerProps> = ({
  colorRules,
}) => {
  const colorByUwiMap = useAtomValue(preDrillColorByUwiMapAtom);

  const uwiColorMap = useMemo(
    () => buildColorByUwiColorMap(colorRules, colorByUwiMap),
    [colorRules, colorByUwiMap]
  );

  // Tile only the matched UWIs (chunked) — the base layer carries the rest.
  const tiles = useMemo(() => {
    const uwis = Object.keys(uwiColorMap);
    if (uwis.length === 0) return [];
    const baseUrl = `${window.location.origin}/queries/wells/mvt`;
    return chunk(uwis, UWI_CHUNK_SIZE).map((group) => {
      const params = new URLSearchParams({ uwis: group.join(',') });
      return `${baseUrl}/survey/{z}/{x}/{y}.mvt?${params.toString()}`;
    });
  }, [uwiColorMap]);

  const colorExpr = useMemo(() => buildColorExpr(uwiColorMap), [uwiColorMap]);
  const fp = useMemo(() => tileFingerprint(tiles), [tiles]);

  if (tiles.length === 0) return null;

  return (
    <>
      {tiles.map((tileUrl, idx) => (
        <Fragment key={`pre-drill-scoped-wells-${fp}-${idx}`}>
          {/* Deviation-survey line (wells with a 2D survey). */}
          <Source
            id={`pre-drill-scoped-wells-survey-${fp}-${idx}`}
            type="vector"
            tiles={[tileUrl]}
          >
            <Layer
              id={`pre-drill-scoped-wells-survey-line-${fp}-${idx}`}
              type="line"
              source-layer="hits"
              filter={MEASURED_SURVEY}
              paint={{
                'line-color': colorExpr,
                'line-width': MATCHED_LINE_WIDTH,
                'line-opacity': 0.85,
              }}
            />
            {/* A survey line with no filed survey behind it is an estimate, dashed
                like the stick below. */}
            <Layer
              id={`pre-drill-scoped-wells-survey-estimated-line-${fp}-${idx}`}
              type="line"
              source-layer="hits"
              filter={ESTIMATED_SURVEY}
              paint={{
                'line-color': colorExpr,
                'line-width': MATCHED_LINE_WIDTH,
                'line-opacity': 0.6,
                'line-dasharray': [1, 1],
              }}
            />
          </Source>

          {/* Stick fallback (surface→bottom-hole for wells without a survey). */}
          <Source
            id={`pre-drill-scoped-wells-stick-${fp}-${idx}`}
            type="vector"
            tiles={[tileUrl.replace('/survey/', '/stick/')]}
          >
            <Layer
              id={`pre-drill-scoped-wells-stick-line-${fp}-${idx}`}
              type="line"
              source-layer="hits"
              paint={{
                'line-color': colorExpr,
                'line-width': MATCHED_LINE_WIDTH,
                'line-opacity': 0.6,
                'line-dasharray': [1, 1],
              }}
            />
          </Source>
        </Fragment>
      ))}
    </>
  );
};
